import React from "react";
import { Link } from "react-router-dom";
import { Compass } from "lucide-react";
import { PageHeader } from "@/design-system/components/PageHeader";
import { EmptyState } from "@/design-system/components/EmptyState";
import { ContentContainer } from "@/design-system/components/ContentContainer";

/**
 * NotFound — canonical 404 page.
 *
 * Rendered by the `*` catch-all route in App.js. Uses the exact same DS
 * primitives every other Executive Complete page uses so 404 stops
 * feeling like it belongs to a different product.
 */

const linkClass =
  "inline-flex items-center gap-2 h-9 px-3 rounded-[var(--ds-radius-md)] " +
  "text-[13px] font-medium border " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)]";

export default function NotFound() {
  return (
    <ContentContainer data-testid="not-found-page" width="narrow">
      <PageHeader
        eyebrow="404"
        title="Page not found"
        description="The page you're looking for doesn't exist or has moved."
        data-testid="not-found-header"
      />
      <EmptyState
        title="We couldn't find what you were looking for"
        description="Check the URL or return to a known surface. If you followed a link inside the app and hit this page, that's a bug we'd like to hear about."
        icon={<Compass className="w-8 h-8 text-[var(--ds-color-ink-subtle)]" aria-hidden />}
        data-testid="not-found-empty"
        action={
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className={`${linkClass} bg-[var(--ds-color-accent)] text-[var(--ds-color-accent-ink)] border-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)]`}
              data-testid="not-found-home"
            >
              Return home
            </Link>
            <Link
              to="/dashboard"
              className={`${linkClass} bg-[var(--ds-color-surface)] text-[var(--ds-color-ink)] border-[var(--ds-color-border-strong)] hover:bg-[var(--ds-color-surface-muted)]`}
              data-testid="not-found-dashboard"
            >
              Go to dashboard
            </Link>
          </div>
        }
      />
    </ContentContainer>
  );
}
